import { Color3, Mesh, MeshBuilder, type Scene, StandardMaterial } from "@babylonjs/core";
import { AdvancedDynamicTexture, Rectangle, TextBlock } from "@babylonjs/gui";

export interface PrimitiveSpec { shape: "box" | "cylinder" | "sphere" | "label"; size: [number, number, number]; color: string; text: string }

const DEFAULT_COLOR = "#c8c8c8";
const SHAPES = new Set(["box", "cylinder", "sphere", "label"]);

/** Reads components.primitive. The server validated it already, so anything odd just means "no primitive". */
export function parsePrimitive(componentsJson: string): PrimitiveSpec | null {
  let c: unknown;
  try { c = JSON.parse(componentsJson || "{}"); } catch { return null; }
  const p = (c as { primitive?: unknown } | null)?.primitive;
  if (!p || typeof p !== "object") return null;
  const { shape, size, color, text } = p as Record<string, unknown>;
  if (typeof shape !== "string" || !SHAPES.has(shape)) return null;
  if (!Array.isArray(size) || size.length !== 3 || !size.every((v) => typeof v === "number" && Number.isFinite(v) && v > 0)) return null;
  return {
    shape: shape as PrimitiveSpec["shape"],
    size: size as [number, number, number],
    color: typeof color === "string" && /^#[0-9a-fA-F]{6}$/.test(color) ? color : DEFAULT_COLOR,
    text: typeof text === "string" ? text.slice(0, 64) : "",
  };
}

/** Builds a primitive resting on y = 0 of its parent. Label text goes through a GUI TextBlock, never HTML. */
export function buildPrimitive(scene: Scene, name: string, spec: PrimitiveSpec): Mesh {
  const [sx, sy, sz] = spec.size;
  let mesh: Mesh;
  switch (spec.shape) {
    case "box":
      mesh = MeshBuilder.CreateBox(name, { width: sx, height: sy, depth: sz }, scene);
      break;
    case "cylinder":
      mesh = MeshBuilder.CreateCylinder(name, { height: sy, diameter: sx, tessellation: 16 }, scene);
      mesh.scaling.z = sz / sx;
      break;
    case "sphere":
      mesh = MeshBuilder.CreateSphere(name, { diameterX: sx, diameterY: sy, diameterZ: sz, segments: 12 }, scene);
      break;
    case "label":
      mesh = MeshBuilder.CreatePlane(name, { width: sx, height: sy, sideOrientation: Mesh.DOUBLESIDE }, scene);
      break;
  }
  mesh.position.y = sy / 2;
  if (spec.shape === "label") {
    const texH = Math.min(1024, Math.max(64, Math.round((512 * sy) / sx)));
    const ui = AdvancedDynamicTexture.CreateForMesh(mesh, 512, texH, false);
    const bg = new Rectangle(name + ":bg");
    bg.background = spec.color;
    bg.thickness = 0;
    ui.addControl(bg);
    const tb = new TextBlock(name + ":text", spec.text);
    tb.color = "#111111";
    tb.fontSize = Math.round(texH * 0.45);
    tb.textWrapping = true;
    bg.addControl(tb);
  } else {
    const mat = new StandardMaterial(name + ":mat", scene);
    mat.diffuseColor = Color3.FromHexString(spec.color);
    mat.specularColor = Color3.Black();
    mesh.material = mat;
  }
  return mesh;
}
